import { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import axios from "axios";
import Navbar from "@/components/Navbar";
import GameCertificate from "@/components/GameCertificate";
import gameSounds from "@/utils/gameSounds";
import { trackGameStarted } from "@/utils/analytics";
import { 
  Play, 
  Trophy, 
  Timer, 
  CheckCircle2,
  XCircle,
  Loader2,
  ArrowRight,
  Zap,
  Award,
  Sparkles,
  Lock,
  Unlock,
  Target,
  Volume2,
  VolumeX
} from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

function GameSoloPage({ user, onLogout }) {
  const navigate = useNavigate();
  const { t } = useTranslation();
  
  // State
  const [modules, setModules] = useState([]);
  const [selectedModule, setSelectedModule] = useState(null);
  const [gameState, setGameState] = useState("select"); // select, playing, complete
  const [sessionId, setSessionId] = useState(null);
  const [currentQuestion, setCurrentQuestion] = useState(null);
  const [questionNumber, setQuestionNumber] = useState(0);
  const [totalQuestions, setTotalQuestions] = useState(0);
  const [totalPoints, setTotalPoints] = useState(0);
  const [selectedAnswer, setSelectedAnswer] = useState(null);
  const [answerSubmitted, setAnswerSubmitted] = useState(false);
  const [answerResult, setAnswerResult] = useState(null);
  const [timeRemaining, setTimeRemaining] = useState(0);
  const [completionData, setCompletionData] = useState(null);
  const [progress, setProgress] = useState(null);
  const [loading, setLoading] = useState(false);
  const [showCertificate, setShowCertificate] = useState(false);
  const [certificate, setCertificate] = useState(null);
  const [businessPlan, setBusinessPlan] = useState(null);
  const [generatingPlan, setGeneratingPlan] = useState(false);
  const [simulationInput, setSimulationInput] = useState("");
  // Streak state
  const [currentStreak, setCurrentStreak] = useState(0);
  const [streakMultiplier, setStreakMultiplier] = useState(1.0);
  const [maxStreak, setMaxStreak] = useState(0);
  const [showStreakAnimation, setShowStreakAnimation] = useState(false);
  // Sound state
  const [soundEnabled, setSoundEnabled] = useState(true);
  
  const timerRef = useRef(null);
  const startTimeRef = useRef(null);

  // Initialize sound settings
  useEffect(() => {
    gameSounds.setEnabled(soundEnabled);
  }, [soundEnabled]);

  // Fetch modules and student progress on mount
  useEffect(() => {
    fetchModules();
    if (user?.id) {
      fetchProgress();
    }
  }, [user]);

  // Timer effect with sound
  useEffect(() => {
    if (currentQuestion && !answerSubmitted && timeRemaining > 0) {
      timerRef.current = setInterval(() => {
        setTimeRemaining(prev => {
          if (prev <= 1) {
            clearInterval(timerRef.current);
            gameSounds.playTimeUp();
            // Auto-submit when time runs out
            if (!answerSubmitted) {
              handleTimeUp();
            }
            return 0;
          }
          // Sound effects for timer
          if (prev <= 5) {
            gameSounds.playWarning();
          } else if (prev <= 10) {
            gameSounds.playTick();
          }
          return prev - 1;
        });
      }, 1000);
      
      return () => clearInterval(timerRef.current);
    }
  }, [currentQuestion, answerSubmitted]);

  const fetchModules = async () => {
    try {
      const response = await axios.get(`${API}/game/modules`);
      setModules(response.data);
    } catch (error) {
      console.error("Failed to fetch modules:", error);
    }
  };

  const fetchProgress = async () => {
    try {
      const response = await axios.get(`${API}/game/student/${user.id}/progress`);
      setProgress(response.data);
    } catch (error) {
      console.error("Failed to fetch progress:", error);
    }
  };

  // Handle module click - start game immediately
  const handleModuleClick = async (module) => {
    if (loading || !user) return;
    setSelectedModule(module);
    setLoading(true);
    
    // Play game start sound
    gameSounds.playGameStart();
    
    try {
      const response = await axios.post(`${API}/game/solo/start`, {
        module_id: module.id,
        student_id: user.id,
        student_name: user.name
      });
      
      setSessionId(response.data.session_id);
      setTotalQuestions(response.data.total_questions);
      setCurrentQuestion(response.data.question);
      setQuestionNumber(0);
      setTimeRemaining(response.data.question.time_limit);
      startTimeRef.current = Date.now();
      // Initialize streak state from API
      setCurrentStreak(response.data.streak || 0);
      setStreakMultiplier(response.data.streak_multiplier || 1.0);
      setMaxStreak(0);
      setGameState("playing");
      
      trackGameStarted({ role: user?.role, moduleName: module.title, gameMode: "solo" });
      
      // Play new question sound after a short delay
      setTimeout(() => gameSounds.playNewQuestion(), 300);
    } catch (error) {
      console.error("Failed to start game:", error);
      alert("Failed to start game. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  // Legacy function for compatibility
  const startGame = async () => {
    if (selectedModule) {
      await handleModuleClick(selectedModule);
    }
  };

  const handleContinue = () => {
    if (window._nextQuestionData) {
      // Play new question sound
      gameSounds.playNewQuestion();
      
      setCurrentQuestion(window._nextQuestionData.question);
      setQuestionNumber(window._nextQuestionData.question_number);
      setTimeRemaining(window._nextQuestionData.question.time_limit);
      startTimeRef.current = Date.now();
      setSelectedAnswer(null);
      setAnswerSubmitted(false);
      setAnswerResult(null);
      setSimulationInput("");
      window._nextQuestionData = null;
    } else {
      // Next question data not ready - this shouldn't normally happen
      // Don't reset the game, just log and let user try again
      console.warn("Next question data not ready yet - please wait");
    }
  };

  const handleTimeUp = async () => {
    // Submit with timeout indicator - allow moving to next question
    const responseTimeMs = currentQuestion.time_limit * 1000; // Indicates timeout
    await handleSubmitAnswer(""); // Empty answer for timeout
  };

  // Updated submit answer with next question handling
  const handleSubmitAnswer = async (answer) => {
    if (answerSubmitted || !sessionId) return;
    
    const responseTimeMs = Date.now() - startTimeRef.current;
    setSelectedAnswer(answer);
    setAnswerSubmitted(true);
    clearInterval(timerRef.current);
    
    try {
      const response = await axios.post(`${API}/game/solo/answer`, {
        session_id: sessionId,
        question_index: questionNumber,
        answer: answer,
        response_time_ms: responseTimeMs
      });
      
      setAnswerResult(response.data.answer_result);
      setTotalPoints(response.data.total_points);
      
      // Play correct/wrong sound
      if (response.data.answer_result?.correct) {
        gameSounds.playCorrect();
        gameSounds.playPoints();
      } else {
        gameSounds.playWrong();
      }
      
      // Update streak state from API response
      setCurrentStreak(response.data.streak || 0);
      setStreakMultiplier(response.data.streak_multiplier || 1.0);
      setMaxStreak(response.data.max_streak || 0);
      
      // Show streak animation when reaching multiplier thresholds
      if (response.data.streak >= 3 && response.data.answer_result?.correct) {
        setShowStreakAnimation(true);
        gameSounds.playStreak(); // Play streak bonus sound
        setTimeout(() => setShowStreakAnimation(false), 2000);
      }
      
      if (response.data.is_complete) {
        // Game complete!
        gameSounds.playGameComplete();
        setCompletionData(response.data.completion);
        setProgress(response.data.completion.progress);
        setGameState("complete");
        
        // Play badge earned sound if new badges
        if (response.data.completion?.badges_earned?.length > 0) {
          setTimeout(() => gameSounds.playBadgeEarned(), 500);
        }
      } else {
        // Store next question data for when user clicks continue
        window._nextQuestionData = response.data.next_question;
      }
    } catch (error) {
      console.error("Failed to submit answer:", error);
      
      // Always reset state first so user can see options again
      setAnswerSubmitted(false);
      setSelectedAnswer(null);
      
      // Check if session was lost (404 error)
      if (error.response?.status === 404) {
        alert("Your game session expired. Please start a new game.");
        resetGame();
        return;
      }
      
      // Show specific error if available
      const errorMsg = error.response?.data?.detail || "Network error. Please try again.";
      alert(`Error: ${errorMsg}`);
    }
  };

  const generateCertificate = async () => {
    if (!completionData?.game_code) return;
    setLoading(true);
    
    try {
      const response = await axios.post(`${API}/game/generate-certificate`, {
        game_code: completionData.game_code,
        player_id: user.id,
        business_name: `${user.name}'s Lemonade Stand`
      });
      
      if (response.data.success) {
        setCertificate(response.data.certificate);
        setShowCertificate(true);
      }
    } catch (error) {
      console.error("Failed to generate certificate:", error);
    } finally {
      setLoading(false);
    }
  };

  const generateBusinessPlan = async () => {
    if (!completionData?.game_code) return;
    setGeneratingPlan(true);
    
    try {
      const response = await axios.post(`${API}/game/generate-business-plan`, {
        game_code: completionData.game_code,
        player_id: user.id,
        business_name: `${user.name}'s Lemonade Stand`
      });
      
      if (response.data.success) {
        setBusinessPlan(response.data);
      }
    } catch (error) {
      console.error("Failed to generate business plan:", error);
    } finally {
      setGeneratingPlan(false);
    }
  };

  const resetGame = () => {
    setGameState("select");
    setSelectedModule(null);
    setSessionId(null);
    setCurrentQuestion(null);
    setQuestionNumber(0);
    setTotalPoints(0);
    setSelectedAnswer(null);
    setAnswerSubmitted(false);
    setAnswerResult(null);
    setCompletionData(null);
    setBusinessPlan(null);
    setCertificate(null);
    fetchProgress();
  };

  const optionColors = {
    A: { bg: "bg-red-500", hover: "hover:bg-red-600" },
    B: { bg: "bg-blue-500", hover: "hover:bg-blue-600" },
    C: { bg: "bg-green-500", hover: "hover:bg-green-600" },
    D: { bg: "bg-yellow-500", hover: "hover:bg-yellow-600" }
  };

  // Module selection screen
  const renderModuleSelection = () => {
    const completedModules = new Set(progress?.completed_modules || []);
    
    return (
      <div className="max-w-4xl mx-auto">
        <div className="text-center mb-8">
          <h1 className="text-3xl font-bold text-gray-900 mb-2">🎮 Solo Practice Mode</h1>
          <p className="text-gray-600">Play through modules at your own pace and earn your business plan!</p>
        </div>

        {/* Progress Overview */}
        {progress && (
          <div className="bg-white rounded-2xl p-6 shadow-lg mb-8">
            <div className="grid grid-cols-3 gap-4 text-center">
              <div>
                <div className="text-3xl font-bold text-yellow-600">{progress.total_points}</div>
                <div className="text-sm text-gray-500">Total Points</div>
              </div>
              <div>
                <div className="text-3xl font-bold text-green-600">{progress.completed_modules?.length || 0}/{modules.length || 4}</div>
                <div className="text-sm text-gray-500">Modules Done</div>
              </div>
              <div>
                <div className="text-3xl font-bold text-purple-600">{progress.games_played || 0}</div>
                <div className="text-sm text-gray-500">Games Played</div>
              </div>
            </div>
            
            {/* Unlock Status */}
            <div className="mt-6 pt-6 border-t border-gray-200">
              <div className="flex justify-center gap-6">
                <div className={`flex items-center gap-2 ${progress.unlocked?.business_plan ? 'text-green-600' : 'text-gray-400'}`}>
                  {progress.unlocked?.business_plan ? <Unlock className="w-5 h-5" /> : <Lock className="w-5 h-5" />}
                  <span className="text-sm font-medium">AI Business Plan</span>
                </div>
                <div className={`flex items-center gap-2 ${progress.unlocked?.certificate ? 'text-green-600' : 'text-gray-400'}`}>
                  {progress.unlocked?.certificate ? <Unlock className="w-5 h-5" /> : <Lock className="w-5 h-5" />}
                  <span className="text-sm font-medium">Certificate</span>
                </div>
              </div>
              {!progress.unlocked?.business_plan && (
                <p className="text-center text-sm text-gray-500 mt-2">
                  Complete the first 3 modules to unlock your AI Business Plan & Certificate!
                </p>
              )}
            </div>
          </div>
        )}

        {/* Module Cards - Click to start immediately */}
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
          {modules.map((module) => {
            const isCompleted = completedModules.has(module.id);
            
            return (
              <button
                key={module.id}
                onClick={() => handleModuleClick(module)}
                disabled={loading}
                data-testid={`solo-module-${module.id}`}
                className={`p-6 rounded-xl border-2 transition-all text-left relative ${
                  loading
                    ? "opacity-50 cursor-not-allowed"
                    : isCompleted
                    ? "border-green-300 bg-green-50 hover:border-green-400 hover:shadow-lg"
                    : "border-gray-200 hover:border-yellow-400 hover:shadow-lg bg-white"
                }`}
              >
                {isCompleted && (
                  <div className="absolute top-2 right-2">
                    <CheckCircle2 className="w-6 h-6 text-green-500" />
                  </div>
                )}
                <div className="text-3xl mb-3">
                  {module.session_number === 1 && "💡"}
                  {module.session_number === 2 && "🦅"}
                  {module.session_number === 3 && "📋"}
                  {module.session_number === 4 && "🎨"}
                  {module.session_number === 5 && "💰"}
                  {module.session_number === 6 && "🚀"}
                  {module.session_number === 7 && "⭐"}
                </div>
                <h3 className="font-bold text-gray-900 mb-1">{module.title}</h3>
                <p className="text-sm text-gray-500 mb-2">{module.description}</p>
                <div className="text-xs text-gray-400">
                  {module.question_count} questions
                </div>
                {isCompleted ? (
                  <div className="mt-2 text-xs text-green-600 font-medium flex items-center gap-1">
                    <CheckCircle2 className="w-3 h-3" /> Completed - Play Again?
                  </div>
                ) : (
                  <div className="mt-2 text-xs text-yellow-600 font-medium flex items-center gap-1">
                    <Play className="w-3 h-3" /> Tap to Play
                  </div>
                )}
              </button>
            );
          })}
        </div>

        {/* Loading indicator */}
        {loading && (
          <div className="text-center py-4">
            <div className="inline-flex items-center gap-2 bg-yellow-100 text-yellow-800 px-4 py-2 rounded-full">
              <Loader2 className="w-5 h-5 animate-spin" />
              <span className="font-semibold">Loading game...</span>
            </div>
          </div>
        )}
      </div>
    );
  };

  // Playing screen
  const renderPlaying = () => {
    if (!currentQuestion) {
      return (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="w-8 h-8 animate-spin text-yellow-500" />
        </div>
      );
    }

    return (
      <div className="max-w-2xl mx-auto">
        {/* Streak Animation Overlay */}
        {showStreakAnimation && (
          <div className="fixed inset-0 flex items-center justify-center z-50 pointer-events-none">
            <div className="bg-gradient-to-r from-orange-500 to-red-500 text-white px-8 py-4 rounded-2xl shadow-2xl animate-bounce">
              <div className="text-4xl font-bold flex items-center gap-2">
                🔥 {currentStreak}x STREAK!
              </div>
              <div className="text-center text-sm opacity-90">
                {streakMultiplier}x Point Multiplier!
              </div>
            </div>
          </div>
        )}

        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-2">
            <span className="bg-yellow-100 text-yellow-800 px-3 py-1 rounded-full text-sm font-semibold">
              Q{questionNumber + 1}/{totalQuestions}
            </span>
            {/* Streak Indicator */}
            {currentStreak >= 2 && (
              <span className={`px-3 py-1 rounded-full text-sm font-semibold flex items-center gap-1 ${
                currentStreak >= 5 ? 'bg-gradient-to-r from-orange-500 to-red-500 text-white' :
                currentStreak >= 3 ? 'bg-orange-100 text-orange-700' :
                'bg-yellow-100 text-yellow-700'
              }`}>
                🔥 {currentStreak}
                {streakMultiplier > 1 && <span className="text-xs">({streakMultiplier}x)</span>}
              </span>
            )}
          </div>
          <div className="flex items-center gap-4">
            {/* Sound Toggle */}
            <button
              onClick={() => setSoundEnabled(!soundEnabled)}
              className={`p-2 rounded-full transition-colors ${
                soundEnabled ? 'bg-green-100 text-green-600' : 'bg-gray-100 text-gray-400'
              }`}
              title={soundEnabled ? 'Sound On' : 'Sound Off'}
              data-testid="sound-toggle"
            >
              {soundEnabled ? <Volume2 className="w-5 h-5" /> : <VolumeX className="w-5 h-5" />}
            </button>
            <div className="bg-yellow-500 px-3 py-1 rounded-full flex items-center gap-1 text-white">
              <Zap className="w-4 h-4" />
              <span className="font-bold">{totalPoints}</span>
            </div>
            {timeRemaining > 0 && !answerSubmitted && (
              <div className={`px-3 py-1 rounded-full flex items-center gap-1 ${
                timeRemaining <= 5 ? "bg-red-500 animate-pulse text-white" : "bg-gray-200 text-gray-700"
              }`}>
                <Timer className="w-4 h-4" />
                <span className="font-bold">{timeRemaining}</span>
              </div>
            )}
          </div>
        </div>

        {/* Question */}
        <div className="bg-gradient-to-br from-purple-600 to-purple-800 rounded-2xl p-6 mb-6 text-white">
          <p className="text-xs uppercase tracking-wide opacity-75 mb-2">
            {currentQuestion.type === "quiz" && "Knowledge Quiz"}
            {currentQuestion.type === "decision" && "Business Decision"}
            {currentQuestion.type === "simulation" && "Business Simulation"}
          </p>
          <h2 className="text-xl font-bold">{currentQuestion.question}</h2>
        </div>

        {/* Answer Result */}
        {answerResult && (
          <div className={`mb-6 p-4 rounded-2xl ${
            answerResult.correct === true ? "bg-green-100 border-2 border-green-300" :
            answerResult.correct === false ? "bg-red-100 border-2 border-red-300" :
            "bg-blue-100 border-2 border-blue-300"
          }`}>
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                {answerResult.correct === true && <CheckCircle2 className="w-6 h-6 text-green-600" />}
                {answerResult.correct === false && <XCircle className="w-6 h-6 text-red-600" />}
                {answerResult.correct === null && <CheckCircle2 className="w-6 h-6 text-blue-600" />}
                <div>
                  <p className="font-bold">
                    {answerResult.correct === true && "Correct! 🎉"}
                    {answerResult.correct === false && "Not quite!"}
                    {answerResult.correct === null && "Answer recorded!"}
                  </p>
                  <p className="text-sm">
                    +{answerResult.points} points
                    {answerResult.streak_bonus > 0 && (
                      <span className="text-orange-600 ml-1">(+{answerResult.streak_bonus} streak bonus!)</span>
                    )}
                  </p>
                </div>
              </div>
              {/* Streak lost indicator */}
              {answerResult.correct === false && currentStreak === 0 && maxStreak > 0 && (
                <div className="text-gray-500 text-sm">
                  Streak reset
                </div>
              )}
            </div>
            {answerResult.explanation && (
              <p className="mt-3 text-sm text-gray-700">{answerResult.explanation}</p>
            )}
          </div>
        )}

        {/* Quiz/Decision Options */}
        {currentQuestion.options && currentQuestion.options.length > 0 && !answerSubmitted && (
          <div className="space-y-3">
            {currentQuestion.options.map((option) => {
              const colors = optionColors[option.id] || { bg: "bg-gray-500", hover: "hover:bg-gray-600" };
              
              return (
                <button
                  key={option.id}
                  onClick={() => {
                    gameSounds.playClick();
                    handleSubmitAnswer(option.id);
                  }}
                  data-testid={`answer-${option.id}`}
                  className={`w-full p-4 rounded-xl text-white font-semibold text-left transition-all ${colors.bg} ${colors.hover}`}
                >
                  <div className="flex items-center gap-3">
                    <span className="bg-white/20 w-10 h-10 rounded-lg flex items-center justify-center text-xl font-bold">
                      {option.id}
                    </span>
                    <span className="flex-1">{option.text}</span>
                  </div>
                </button>
              );
            })}
          </div>
        )}

        {/* Fallback: No options available but answer not submitted - something went wrong */}
        {(!currentQuestion.options || currentQuestion.options.length === 0) && 
         !answerSubmitted && 
         currentQuestion.type !== "simulation" && (
          <div className="bg-yellow-100 border-2 border-yellow-400 rounded-xl p-6 text-center">
            <p className="text-yellow-800 font-semibold mb-4">Question loading issue detected</p>
            <button
              onClick={resetGame}
              className="bg-yellow-500 hover:bg-yellow-600 text-white px-6 py-2 rounded-full font-semibold"
            >
              Return to Module Selection
            </button>
          </div>
        )}

        {/* Simulation Input */}
        {currentQuestion.type === "simulation" && currentQuestion.simulation_config && !answerSubmitted && (
          <div className="bg-white rounded-2xl p-6 shadow-lg">
            {currentQuestion.simulation_config.type === "text_input" && (
              <>
                <input
                  type="text"
                  value={simulationInput}
                  onChange={(e) => setSimulationInput(e.target.value)}
                  placeholder={currentQuestion.simulation_config.placeholder}
                  maxLength={currentQuestion.simulation_config.max_length}
                  className="w-full px-4 py-3 border-2 border-gray-300 rounded-xl focus:ring-2 focus:ring-purple-500 focus:border-transparent text-lg mb-4"
                />
                <button
                  onClick={() => {
                    gameSounds.playClick();
                    handleSubmitAnswer(simulationInput);
                  }}
                  disabled={!simulationInput.trim()}
                  className="w-full bg-purple-600 hover:bg-purple-700 disabled:bg-gray-300 text-white py-3 rounded-xl font-bold transition-all"
                >
                  Submit Answer
                </button>
              </>
            )}

            {currentQuestion.simulation_config.type === "slider" && (
              <>
                <div className="text-center mb-4">
                  <span className="text-4xl font-bold text-purple-600">
                    {simulationInput || currentQuestion.simulation_config.default}
                  </span>
                  <span className="text-gray-500 ml-2">
                    {currentQuestion.simulation_config.unit}
                  </span>
                </div>
                <input
                  type="range"
                  value={simulationInput || currentQuestion.simulation_config.default}
                  onChange={(e) => setSimulationInput(e.target.value)}
                  min={currentQuestion.simulation_config.min}
                  max={currentQuestion.simulation_config.max}
                  step={currentQuestion.simulation_config.step}
                  className="w-full mb-4"
                />
                <button
                  onClick={() => handleSubmitAnswer(simulationInput || currentQuestion.simulation_config.default)}
                  className="w-full bg-purple-600 hover:bg-purple-700 text-white py-3 rounded-xl font-bold transition-all"
                >
                  Submit Answer
                </button>
              </>
            )}
          </div>
        )}

        {/* Continue Button */}
        {answerSubmitted && !completionData && (
          <div className="text-center mt-6">
            <button
              onClick={handleContinue}
              className="bg-yellow-500 hover:bg-yellow-600 text-white px-8 py-3 rounded-full font-bold text-lg inline-flex items-center gap-2 transition-all"
            >
              Next Question
              <ArrowRight className="w-5 h-5" />
            </button>
          </div>
        )}
      </div>
    );
  };

  // Completion screen
  const renderComplete = () => (
    <div className="max-w-2xl mx-auto text-center">
      <div className="text-6xl mb-4">🎉</div>
      <h1 className="text-3xl font-bold text-gray-900 mb-2">Module Complete!</h1>
      <p className="text-gray-600 mb-8">Great job finishing {selectedModule?.title}!</p>

      {/* Score Card */}
      <div className="bg-gradient-to-br from-yellow-400 to-orange-500 rounded-3xl p-8 text-white mb-8">
        <div className="text-5xl font-bold mb-2">{completionData?.total_points || totalPoints}</div>
        <div className="text-xl opacity-90">Total Points</div>
        {completionData?.disc_type && (
          <div className="mt-4 bg-white/20 rounded-full px-4 py-2 inline-flex items-center gap-2">
            <span className="text-2xl">
              {completionData.disc_type === 'eagle' && '🦅'}
              {completionData.disc_type === 'parrot' && '🦜'}
              {completionData.disc_type === 'dove' && '🕊️'}
              {completionData.disc_type === 'owl' && '🦉'}
            </span>
            <span className="font-semibold capitalize">{completionData.disc_type}</span>
          </div>
        )}
      </div>

      {/* Progress Update */}
      {progress && (
        <div className="bg-white rounded-2xl p-6 shadow-lg mb-8">
          <h3 className="font-bold text-gray-900 mb-4">Your Progress</h3>
          <div className="grid grid-cols-3 gap-4 mb-4">
            <div className="text-center">
              <div className="text-2xl font-bold text-green-600">
                {progress.completed_modules?.length || 0}/{modules.length || 4}
              </div>
              <div className="text-xs text-gray-500">Modules</div>
            </div>
            <div className="text-center">
              <div className="text-2xl font-bold text-yellow-600">
                {progress.total_points}
              </div>
              <div className="text-xs text-gray-500">Total Points</div>
            </div>
            <div className="text-center">
              <div className="text-2xl font-bold text-purple-600">
                {progress.games_played}
              </div>
              <div className="text-xs text-gray-500">Games</div>
            </div>
          </div>
          
          {/* Unlock Status */}
          <div className="flex justify-center gap-4 pt-4 border-t border-gray-200">
            <div className={`px-4 py-2 rounded-full ${progress.unlocked?.business_plan ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-500'}`}>
              {progress.unlocked?.business_plan ? <Unlock className="w-4 h-4 inline mr-1" /> : <Lock className="w-4 h-4 inline mr-1" />}
              Business Plan
            </div>
            <div className={`px-4 py-2 rounded-full ${progress.unlocked?.certificate ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-500'}`}>
              {progress.unlocked?.certificate ? <Unlock className="w-4 h-4 inline mr-1" /> : <Lock className="w-4 h-4 inline mr-1" />}
              Certificate
            </div>
          </div>
        </div>
      )}

      {/* AI Business Plan */}
      {businessPlan && (
        <div className="bg-white rounded-2xl p-6 shadow-lg mb-8 text-left">
          <h3 className="font-bold text-xl text-gray-900 mb-4 flex items-center gap-2">
            <Sparkles className="w-6 h-6 text-purple-500" />
            Your AI Business Plan
          </h3>
          <div className="prose prose-sm max-w-none text-gray-700 whitespace-pre-wrap">
            {businessPlan.business_plan}
          </div>
        </div>
      )}

      {/* Action Buttons */}
      <div className="flex flex-wrap gap-4 justify-center">
        {progress?.unlocked?.certificate && (
          <button
            onClick={generateCertificate}
            disabled={loading}
            className="bg-yellow-500 hover:bg-yellow-600 text-white px-6 py-3 rounded-full font-semibold flex items-center gap-2 transition-all disabled:opacity-50"
          >
            <Award className="w-5 h-5" />
            Get Certificate
          </button>
        )}
        {progress?.unlocked?.business_plan && !businessPlan && (
          <button
            onClick={generateBusinessPlan}
            disabled={generatingPlan}
            className="bg-purple-500 hover:bg-purple-600 text-white px-6 py-3 rounded-full font-semibold flex items-center gap-2 transition-all disabled:opacity-50"
          >
            {generatingPlan ? (
              <Loader2 className="w-5 h-5 animate-spin" />
            ) : (
              <Sparkles className="w-5 h-5" />
            )}
            Generate Business Plan
          </button>
        )}
        <button
          onClick={resetGame}
          className="bg-gray-200 hover:bg-gray-300 text-gray-700 px-6 py-3 rounded-full font-semibold transition-all"
        >
          {progress?.completed_modules?.length >= modules.length ? "Play Again" : "Continue Playing"}
        </button>
      </div>

      {/* Certificate Modal */}
      {showCertificate && certificate && (
        <GameCertificate 
          certificate={certificate}
          onClose={() => setShowCertificate(false)}
        />
      )}
    </div>
  );

  return (
    <div className="min-h-screen bg-gradient-to-br from-yellow-50 to-orange-50">
      <Navbar user={user} onLogout={onLogout} />
      
      <main className="max-w-7xl mx-auto px-4 py-8">
        {gameState === "select" && renderModuleSelection()}
        {gameState === "playing" && renderPlaying()}
        {gameState === "complete" && renderComplete()}
      </main>
    </div>
  );
}

export default GameSoloPage;
